import { CheckIcon } from "@icons";
import {
    CheckboxIndicator,
    type CheckboxIndicatorState,
} from "@design-system/Checkbox/CheckboxIndicator";
import { usePickerRoot } from "./PickerContext";
import type { PickerItemValue } from "./Picker.types";
import styles from "./PickerItem.module.scss";

const PickerItemCheckbox = ({
    state,
    isDisabled,
}: {
    state: CheckboxIndicatorState;
    isDisabled: boolean;
}) => (
    <span className={styles.itemCheckbox} data-picker-part="checkbox">
        <CheckboxIndicator state={state} disabled={isDisabled} />
    </span>
);

/**
 * A row's selection mark, drawn from the root's selection rather than Base
 * UI's per-item state so a leaf selected through a chain (or matched by text
 * under `allowCustomValue`) shows it in every level. Multi-select rows and
 * cascade parents draw a checkbox; single-select rows and rows of an
 * exclusive section draw the check, keeping its slot empty while unselected
 * so trailing content lines up across rows.
 */
export const PickerItemIndicator = ({
    value,
    isSelectable,
    isSelected,
    isDisabled,
    cascadeState,
}: {
    value: PickerItemValue | undefined;
    isSelectable: boolean;
    isSelected: boolean;
    isDisabled: boolean;
    /** Set on a cascade parent: the checkbox state its leaves add up to. */
    cascadeState?: CheckboxIndicatorState;
}) => {
    const root = usePickerRoot();
    if (cascadeState !== undefined) {
        return (
            <PickerItemCheckbox state={cascadeState} isDisabled={isDisabled} />
        );
    }
    if (!isSelectable || value === undefined) {
        return null;
    }
    const isExclusive = root.exclusiveSiblings?.has(value) === true;
    if (root.isMultiple && !isExclusive) {
        const hasSelectedDescendant =
            root.indicatedDescendants
                ?.get(value)
                ?.some((descendant) => root.isSelected(descendant)) === true;
        let state: CheckboxIndicatorState = "unchecked";
        if (isSelected) {
            state = "checked";
        } else if (hasSelectedDescendant) {
            state = "indeterminate";
        }
        return <PickerItemCheckbox state={state} isDisabled={isDisabled} />;
    }
    return (
        <span
            className={styles.itemIndicator}
            data-picker-part={isSelected ? "check" : undefined}
            aria-hidden
        >
            {isSelected && <CheckIcon className={styles.itemCheck} />}
        </span>
    );
};
